import { useEffect, useState } from "react";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import { NewsletterSection } from "@/components/NewsletterForm";
import { PageIntro, FadeUp } from "@/components/Reveal";
import { Download } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { api } from "@/lib/api";

const downloadCsv = (r) => {
    const rows = [["Panther City Clubhouse — Monthly Summary", r.month_label], []];
    const section = (name, lines) => {
        rows.push([name]);
        lines.forEach((l) => rows.push([l]));
        rows.push([]);
    };
    section("Income", r.income_lines || []);
    section("Expenses", r.expense_lines || []);
    if (r.reserves) rows.push(["Reserves", r.reserves]);
    if (r.reinvestment) rows.push(["Community Reinvestment", r.reinvestment]);
    if (r.programs) rows.push(["Program Funding", r.programs]);
    if (r.note) rows.push(["Notes", r.note]);
    const csv = rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `pcch-books-${r.month_label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
};

const LineList = ({ title, lines, accent }) => (
    <div>
        <h4 className={`font-display text-lg tracking-wider ${accent}`}>{title}</h4>
        <ul className="mt-2 space-y-1.5">
            {(lines || []).map((l, i) => (
                <li key={i} className="border-b border-dotted border-ink/25 pb-1 text-sm text-ink/80">{l}</li>
            ))}
        </ul>
    </div>
);

const Transparency = () => {
    const { t } = useContent();
    const [reports, setReports] = useState(null);

    useEffect(() => {
        api.get("/transparency").then((r) => setReports(r.data)).catch(() => setReports([]));
    }, []);

    return (
        <>
            <Seo
                title="The Books, Open"
                description="Panther City Clubhouse posts plain-language monthly money summaries — what came in, what went out, what's getting fixed next."
            />
            <PageIntro kicker={t("transparency.kicker", "RADICAL FINANCIAL TRANSPARENCY")} title="THE BOOKS, OPEN" intro={t("transparency.body", "")} />

            <section className="px-5 pb-16 sm:px-10">
                <div className="mx-auto max-w-4xl space-y-10">
                    {reports === null && <p className="font-hand text-2xl text-ink/60">Dusting the ledger…</p>}
                    {reports && reports.length === 0 && (
                        <FadeUp>
                            <div data-testid="transparency-empty" className="border-2 border-dashed border-ink/40 bg-paper p-10 text-center hard-shadow -rotate-[0.5deg]">
                                <p className="font-display text-3xl tracking-wide text-ink">FIRST REPORT LANDS AFTER OUR FIRST FULL MONTH</p>
                                <p className="mt-3 font-hand text-2xl text-brick">the corkboard is ready. the numbers are coming.</p>
                            </div>
                        </FadeUp>
                    )}
                    {(reports || []).map((r, i) => (
                        <FadeUp key={r.id} delay={0.05}>
                            <article data-testid={`report-card-${i}`} className={`relative border-2 border-ink bg-paper p-7 hard-shadow-lg sm:p-10 ${i % 2 ? "rotate-[0.5deg]" : "-rotate-[0.5deg]"}`}>
                                <span className="tape-strip" aria-hidden="true" />
                                <div className="flex flex-wrap items-center justify-between gap-4">
                                    <h3 className="font-display text-3xl tracking-wide text-ink sm:text-4xl">{r.month_label}</h3>
                                    <button onClick={() => downloadCsv(r)} data-testid={`report-download-${i}`} className="flex items-center gap-2 border-2 border-ink bg-mustard px-4 py-2 font-display text-sm tracking-[0.12em] text-ink hard-shadow transition-transform duration-150 hover:-translate-y-0.5">
                                        <Download className="h-4 w-4" /> DOWNLOAD CSV
                                    </button>
                                </div>
                                <div className="mt-6 grid gap-6 sm:grid-cols-2">
                                    <LineList title="INCOME" lines={r.income_lines} accent="text-forest" />
                                    <LineList title="EXPENSES" lines={r.expense_lines} accent="text-brick" />
                                </div>
                                <div className="mt-6 space-y-2 text-sm text-ink/80">
                                    {r.reserves ? <p><span className="font-display tracking-wider text-ink">RESERVES:</span> {r.reserves}</p> : null}
                                    {r.reinvestment ? <p><span className="font-display tracking-wider text-ink">COMMUNITY REINVESTMENT:</span> {r.reinvestment}</p> : null}
                                    {r.programs ? <p><span className="font-display tracking-wider text-ink">PROGRAM FUNDING:</span> {r.programs}</p> : null}
                                </div>
                                {r.note ? <p className="mt-5 border-t-2 border-dashed border-ink/30 pt-4 font-hand text-xl text-brick">{r.note}</p> : null}
                            </article>
                        </FadeUp>
                    ))}

                    <FadeUp>
                        <div className="border-2 border-ink bg-forest p-7 text-cream hard-shadow-lg sm:p-9">
                            <h3 className="font-display text-2xl tracking-wide">{t("about.org_heading", "WHO ACTUALLY RUNS THIS PLACE")}</h3>
                            <p className="mt-3 leading-relaxed text-cream/85">{t("about.org_llc", "")}</p>
                            <p className="mt-3 leading-relaxed text-cream/85">{t("about.org_alliance", "")}</p>
                        </div>
                    </FadeUp>
                </div>
            </section>
            <Marquee />
            <NewsletterSection idPrefix="transparency-newsletter" />
        </>
    );
};

export default Transparency;
